import { queryOptions, useQuery } from "@tanstack/react-query";

import stellar from "@/services/stellar";
import type { Purchase } from "../types";

export const PURCHASES_QUERY_KEY = ["purchases"] as const;

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 100;

const buildHasMore = (page: number, limit: number, total: number) =>
	page * limit < total;

type PurchasesQueryVariables = {
	page: number;
	limit: number;
	withCompleted: boolean;
};

type PurchasesQueryResponse = {
	data?: Purchase[] | null;
	total?: number | null;
};

type PurchasesQueryResult = {
	purchases: Purchase[];
	total: number;
	page: number;
	limit: number;
	withCompleted: boolean;
	hasMore: boolean;
};

const fetchPurchases = async (
	variables: PurchasesQueryVariables,
): Promise<PurchasesQueryResult> => {
	const response = (await stellar.action("paginatePurchases", variables)) as
		| PurchasesQueryResponse
		| null
		| undefined;

	const purchases = Array.isArray(response?.data) ? (response?.data ?? []) : [];
	const total = Number(response?.total ?? 0);

	return {
		purchases,
		total,
		page: variables.page,
		limit: variables.limit,
		withCompleted: variables.withCompleted,
		hasMore: buildHasMore(variables.page, variables.limit, total),
	};
};

const buildPurchasesQueryKey = (variables: PurchasesQueryVariables) =>
	[...PURCHASES_QUERY_KEY, variables] as const;

export const purchasesQueryOptions = (variables: PurchasesQueryVariables) =>
	queryOptions({
		queryKey: buildPurchasesQueryKey(variables),
		queryFn: () => fetchPurchases(variables),
	});

type UsePurchasesQueryParams = Partial<PurchasesQueryVariables> & {
	enabled?: boolean;
};

export const usePurchasesQuery = ({
	page = DEFAULT_PAGE,
	limit = DEFAULT_LIMIT,
	withCompleted = false,
	enabled = true,
}: UsePurchasesQueryParams = {}) =>
	useQuery({
		...purchasesQueryOptions({ page, limit, withCompleted }),
		enabled,
	});
